import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  WEAK_PASSWORD_STATUS,
  OKAY_PASSWORD_STATUS,
  STRONG_PASSWORD_STATUS,
} from '~/components/ui/PasswordField/constants'
import {
  getPasswordStatus,
  capitalize,
} from '~/components/ui/PasswordField/utils'
import PasswordFieldValidations from '~/components/ui/PasswordField/Validations'
import TextField from '~/components/ui/TextField'
import styles from './styles.module.scss'

const PasswordField = React.forwardRef((props, ref) => {
  const {
    minStrength,
    thresholdLength,
    value,
    label,
    showValidations,
    name
  } = props

  const [showPassword, setShowPassword] = useState(false)
  const toggleShowPassword = useCallback(() => setShowPassword(prevValue => !prevValue), [setShowPassword])

  const status = getPasswordStatus(value, minStrength, thresholdLength)

  return (
    <div className={`${styles['password-container']}`}>
      <TextField
        ref={ref}
        type={showPassword ? 'text' : 'password'}
        autoComplete="on"
        required
        label={label}
        name={name}
        endAdornment={(
          <FontAwesomeIcon
            icon={`far ${showPassword ? 'fa-eye-slash' : 'fa-eye'} `}
            onClick={toggleShowPassword}
            data-testid={`stellar-password-${showPassword ? 'closed' : 'opened'}-eye-icon`}
          />
        )}
        status={(
          <span
            className={classNames({
              [styles['weak-label']]: status === WEAK_PASSWORD_STATUS,
              [styles['okay-label']]: status === OKAY_PASSWORD_STATUS,
              [styles['strong-label']]: status === STRONG_PASSWORD_STATUS,
            })}
          >
            {capitalize(status)}
          </span>
        )}
        {...props}
      />

      {value && showValidations ? (
        <PasswordFieldValidations value={value} />
      ) : null}
    </div>
  )
})

PasswordField.propTypes = {
  value: PropTypes.string.isRequired,
  name: PropTypes.string,
  minStrength: PropTypes.number,
  thresholdLength: PropTypes.number,
  label: PropTypes.string,
  showValidations: PropTypes.bool,
}

PasswordField.defaultProps = {
  minStrength: 3,
  thresholdLength: 8,
  label: 'Password',
  name: 'Password',
  showValidations: true,
}

export default PasswordField
